/**
 * Wallet overview - the numbers an operator checks daily.
 *
 * Two kinds of figure live here and they are deliberately separated:
 *
 *   LIABILITY is point-in-time. "What do we owe customers right now." It is NOT
 *   date-filtered, because filtering a balance-sheet figure to a window reports
 *   a fragment as if it were the whole thing.
 *   MOVEMENT is the window. Credits, debits, and where each came from.
 *
 * The credit breakdown is what makes the liability explainable: how much of it
 * customers funded, how much we gave away, and how much is money we owe back.
 */
import { useCallback, useEffect, useState } from 'react'

import LoadErrorState from '../../components/common/LoadErrorState'
import { Card, CardTitle, StatCard, StatRow, Td } from '../../features/b2b/components/primitives'
import { useColors } from '../../hooks/useColors'
import { formatINR, getWalletSummary } from '../../services/walletApi'
import type { DateRangeFilter, WalletSummary } from '../../services/walletApi'

export default function WalletOverviewTab({ filter }: { filter: DateRangeFilter }) {
  const colors = useColors()
  const [summary, setSummary] = useState<WalletSummary | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      setSummary(await getWalletSummary(filter))
    } catch {
      setError('Could not load the wallet summary.')
    } finally {
      setLoading(false)
    }
  }, [filter])

  useEffect(() => {
    void load()
  }, [load])

  if (error) return <LoadErrorState message={error} onRetry={load} />
  if (loading && !summary) return <Card><span style={{ color: colors.textMuted }}>Loading…</span></Card>
  if (!summary) return null

  const creditRows: Array<[string, number, string]> = [
    ['Top-ups (customer money)', summary.credits_breakdown.topup, 'Real money collected through the gateway'],
    ['Top-up bonuses', summary.credits_breakdown.topup_bonus, 'Promotional. Our cost, not a customer deposit'],
    ['Referral rewards', summary.credits_breakdown.referral_rewards, 'Paid to referrers and their friends'],
    ['Refunds to wallet', summary.credits_breakdown.refunds, 'Money returned from cancelled orders'],
    ['Manual credits', summary.credits_breakdown.admin_credits, 'Adjustments posted by an admin'],
  ]

  const debitRows: Array<[string, number, string]> = [
    ['Order payments', summary.debits_breakdown.order_payments, 'Wallet spent at checkout'],
    ['Manual debits', summary.debits_breakdown.admin_debits, 'Adjustments posted by an admin'],
    ['Reward clawbacks', summary.debits_breakdown.reward_reversals, 'Rewards reversed after a cancelled order'],
  ]

  return (
    <>
      {/* Point-in-time. Never date-filtered - see the header. */}
      <StatRow>
        <StatCard
          label="Outstanding liability"
          value={formatINR(summary.outstanding_liability)}
          hint={`${summary.wallets_with_balance} of ${summary.total_wallets} wallets hold a balance`}
        />
        <StatCard
          label="Customer-funded"
          value={formatINR(summary.customer_funded_balance)}
          hint="Money customers paid us. Refundable"
        />
        <StatCard
          label="Promotional"
          value={formatINR(summary.promotional_balance)}
          color="#B45309"
          hint="Bonuses and rewards we issued. Not refundable"
        />
      </StatRow>

      <StatRow>
        <StatCard label="Credits in period" value={formatINR(summary.total_credits)} color="#15803D" />
        <StatCard label="Debits in period" value={formatINR(summary.total_debits)} />
        <StatCard
          label="Net change"
          value={formatINR(summary.net_change)}
          color={summary.net_change >= 0 ? '#15803D' : '#991B1B'}
          hint={`${summary.transaction_count} transactions · ${summary.active_users} customers`}
        />
      </StatRow>

      <StatRow>
        <StatCard
          label="Top-up revenue"
          value={formatINR(summary.topups.revenue_collected)}
          color="#15803D"
          hint={`${summary.topups.successful} successful, ${summary.topups.failed} failed`}
        />
        <StatCard
          label="Promotional bonus issued"
          value={formatINR(summary.topups.promotional_bonus_issued)}
          color="#B45309"
          hint="The discount customers received on top-ups"
        />
        <StatCard
          label="Wallet credited from top-ups"
          value={formatINR(summary.topups.revenue_collected + summary.topups.promotional_bonus_issued)}
          hint="Revenue + bonus"
        />
      </StatRow>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '16px' }}>
        <Card>
          <CardTitle>Where the credits came from</CardTitle>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
              {creditRows.map(([label, amount, hint]) => (
                <tr key={label}>
                  <Td>
                    <div>{label}</div>
                    <div style={{ fontSize: '12px', color: colors.textMuted }}>{hint}</div>
                  </Td>
                  <Td align="right" strong>{formatINR(amount)}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>

        <Card>
          <CardTitle>Where the debits went</CardTitle>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
              {debitRows.map(([label, amount, hint]) => (
                <tr key={label}>
                  <Td>
                    <div>{label}</div>
                    <div style={{ fontSize: '12px', color: colors.textMuted }}>{hint}</div>
                  </Td>
                  <Td align="right" strong>{formatINR(amount)}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      </div>
    </>
  )
}
